import React, { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { registerClient } from '../services/api';
import Header from '../components/Header';
import Footer from '../components/Footer';

export default function Register() {
  const navigate = useNavigate();
  const [form, setForm] = useState({
    civilite: 'M.',
    nom: '',
    prenom: '',
    email: '',
    password: '',
    confirmPassword: '',
  });
  const [error, setError] = useState('');
  const [success, setSuccess] = useState(false);
  const [loading, setLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirm, setShowConfirm] = useState(false);

  const passwordRules = [
    { label: '8 caractères minimum', ok: form.password.length >= 8 },
    { label: '1 majuscule minimum', ok: /[A-Z]/.test(form.password) },
    { label: '1 chiffre minimum', ok: /[0-9]/.test(form.password) },
  ];

  const handleChange = (e) => {
    setForm({ ...form, [e.target.name]: e.target.value });
    setError('');
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');

    if (form.password !== form.confirmPassword) {
      setError('Les mots de passe ne correspondent pas.');
      return;
    }

    setLoading(true);
    try {
      await registerClient(form);
      setSuccess(true);
    } catch (err) {
      setError(err.response?.data?.error || 'Une erreur est survenue.');
    } finally {
      setLoading(false);
    }
  };

  if (success) {
    return (
      <>
        <Header />
        <div className="min-h-screen bg-gray-50 flex items-center justify-center px-4">
          <div className="bg-white rounded-2xl shadow-xl p-10 max-w-md w-full text-center">
            <div className="w-20 h-20 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-6">
              <svg className="w-10 h-10 text-green-500" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
              </svg>
            </div>
            <h2 className="text-2xl font-bold text-gray-900 mb-3">Vérifiez votre email !</h2>
            <p className="text-gray-600 mb-8 leading-relaxed">
              Un email de confirmation a été envoyé à <strong>{form.email}</strong>.<br/>
              Cliquez sur le lien dans l'email pour activer votre compte.
            </p>
            <Link to="/connexion" className="inline-block bg-black text-white px-8 py-3 rounded-lg font-bold hover:bg-gray-800 transition">
              Se connecter
            </Link>
          </div>
        </div>
        <Footer />
      </>
    );
  }

  return (
    <>
      <Header />
      <div className="min-h-screen bg-gray-50 py-12 px-4">
        <div className="max-w-5xl mx-auto">
          <div className="bg-white rounded-2xl shadow-xl overflow-hidden flex flex-col md:flex-row">
            {/* Panneau gauche */}
            <div className="hidden md:flex md:w-2/5 bg-black flex-col justify-center p-12">
              <p className="text-3xl font-black tracking-widest text-white uppercase mb-2">
                ROYAL <span className="text-yellow-400">CARS</span>
              </p>
              <p className="text-gray-400 text-sm uppercase tracking-widest mb-10">Votre espace personnel</p>
              <ul className="space-y-5">
                {[
                  { icon: '♥', text: 'Sauvegardez vos véhicules favoris' },
                  { icon: '🔔', text: 'Suivez les nouveaux arrivages' },
                  { icon: '🔒', text: 'Compte sécurisé et protégé' },
                  { icon: '⚡', text: 'Accès rapide à vos sélections' },
                ].map(({ icon, text }) => (
                  <li key={text} className="flex items-start gap-3">
                    <span className="text-yellow-400 text-xl">{icon}</span>
                    <span className="text-gray-300 text-sm leading-relaxed">{text}</span>
                  </li>
                ))}
              </ul>
              <div className="mt-10 pt-8 border-t border-gray-800">
                <p className="text-gray-500 text-sm">Déjà un compte ?</p>
                <Link to="/connexion" className="text-yellow-400 font-bold hover:text-yellow-300 transition text-sm">
                  Se connecter →
                </Link>
              </div>
            </div>

            {/* Formulaire */}
            <div className="flex-1 p-8 md:p-12">
              <div className="md:hidden mb-6">
                <p className="text-2xl font-black tracking-widest text-black uppercase">
                  ROYAL <span className="text-yellow-400">CARS</span>
                </p>
              </div>
              <h1 className="text-2xl font-bold text-gray-900 mb-1">Créer un compte</h1>
              <p className="text-gray-500 text-sm mb-8">Remplissez le formulaire ci-dessous pour vous inscrire.</p>

              {error && (
                <div className="bg-red-50 border border-red-200 rounded-lg p-4 mb-6 flex items-start gap-3">
                  <span className="text-red-500 text-lg">⚠</span>
                  <p className="text-red-700 text-sm">{error}</p>
                </div>
              )}

              <form onSubmit={handleSubmit} className="space-y-5">
                {/* Civilité */}
                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-2">Civilité *</label>
                  <div className="flex gap-3">
                    {['M.', 'Mme'].map(c => (
                      <label key={c} className={`flex-1 flex items-center justify-center gap-2 border-2 rounded-lg py-3 cursor-pointer transition font-semibold text-sm ${
                        form.civilite === c ? 'border-black bg-black text-white' : 'border-gray-200 text-gray-600 hover:border-gray-400'
                      }`}>
                        <input type="radio" name="civilite" value={c} checked={form.civilite === c} onChange={handleChange} className="hidden" />
                        {c}
                      </label>
                    ))}
                  </div>
                </div>

                {/* Nom / Prénom */}
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-semibold text-gray-700 mb-2">Nom *</label>
                    <input
                      type="text" name="nom" value={form.nom} onChange={handleChange} required
                      placeholder="DUPONT"
                      className="w-full border-2 border-gray-200 rounded-lg px-4 py-3 text-sm focus:border-black focus:outline-none transition"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-semibold text-gray-700 mb-2">Prénom *</label>
                    <input
                      type="text" name="prenom" value={form.prenom} onChange={handleChange} required
                      placeholder="Jean"
                      className="w-full border-2 border-gray-200 rounded-lg px-4 py-3 text-sm focus:border-black focus:outline-none transition"
                    />
                  </div>
                </div>

                {/* Email */}
                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-2">Adresse email *</label>
                  <input
                    type="email" name="email" value={form.email} onChange={handleChange} required
                    placeholder="jean.dupont@email.com"
                    className="w-full border-2 border-gray-200 rounded-lg px-4 py-3 text-sm focus:border-black focus:outline-none transition"
                  />
                </div>

                {/* Mot de passe */}
                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-2">Mot de passe *</label>
                  <div className="relative">
                    <input
                      type={showPassword ? 'text' : 'password'} name="password" value={form.password} onChange={handleChange} required
                      placeholder="••••••••"
                      className="w-full border-2 border-gray-200 rounded-lg px-4 py-3 pr-12 text-sm focus:border-black focus:outline-none transition"
                    />
                    <button type="button" onClick={() => setShowPassword(!showPassword)}
                      className="absolute right-4 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600">
                      {showPassword ? '🙈' : '👁'}
                    </button>
                  </div>
                  {form.password && (
                    <div className="mt-2 flex gap-3 flex-wrap">
                      {passwordRules.map(rule => (
                        <span key={rule.label} className={`text-xs flex items-center gap-1 ${rule.ok ? 'text-green-600' : 'text-gray-400'}`}>
                          <span>{rule.ok ? '✓' : '○'}</span> {rule.label}
                        </span>
                      ))}
                    </div>
                  )}
                </div>

                {/* Confirmer MDP */}
                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-2">Confirmer le mot de passe *</label>
                  <div className="relative">
                    <input
                      type={showConfirm ? 'text' : 'password'} name="confirmPassword" value={form.confirmPassword} onChange={handleChange} required
                      placeholder="••••••••"
                      className={`w-full border-2 rounded-lg px-4 py-3 pr-12 text-sm focus:outline-none transition ${
                        form.confirmPassword && form.password !== form.confirmPassword
                          ? 'border-red-400 focus:border-red-400'
                          : form.confirmPassword && form.password === form.confirmPassword
                          ? 'border-green-400 focus:border-green-400'
                          : 'border-gray-200 focus:border-black'
                      }`}
                    />
                    <button type="button" onClick={() => setShowConfirm(!showConfirm)}
                      className="absolute right-4 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600">
                      {showConfirm ? '🙈' : '👁'}
                    </button>
                  </div>
                  {form.confirmPassword && form.password !== form.confirmPassword && (
                    <p className="text-red-500 text-xs mt-1">Les mots de passe ne correspondent pas</p>
                  )}
                </div>

                <button
                  type="submit" disabled={loading}
                  className="w-full bg-black text-white py-4 rounded-lg font-bold text-sm hover:bg-gray-800 transition disabled:opacity-50 disabled:cursor-not-allowed mt-2"
                >
                  {loading ? 'Création en cours...' : 'Créer mon compte'}
                </button>

                <p className="text-center text-sm text-gray-500">
                  Déjà un compte ?{' '}
                  <Link to="/connexion" className="text-yellow-500 font-bold hover:text-yellow-400 transition">
                    Se connecter
                  </Link>
                </p>
              </form>
            </div>
          </div>
        </div>
      </div>
      <Footer />
    </>
  );
}
